import React from "react";

import { VitalsRangeStripProps } from "@/types/vitals";
import styles from "./VitalsRangeStrip.module.css";

const OUT_OF_RANGE_COLOR = "#d64561";
/** Keeps the marker and band off the rounded ends of the track. */
const EDGE_INSET_PERCENT = 2;

function toPercent(value: number, [lower, upper]: [number, number]): number {
  const span = upper - lower;
  if (span <= 0) return 50;
  const raw = ((value - lower) / span) * 100;
  return Math.min(100 - EDGE_INSET_PERCENT, Math.max(EDGE_INSET_PERCENT, raw));
}

/**
 * A single reading shown against its reference range. Used instead of the line
 * chart until there are two readings to draw a trend between.
 */
const VitalsRangeStrip: React.FC<VitalsRangeStripProps> = ({
  label,
  value,
  unit,
  color,
  domain,
  range,
  rangeLabel,
  outOfRange,
}) => {
  const markerLeft = toPercent(value, domain);
  const bandStart = range ? toPercent(range[0], domain) : 0;
  const bandEnd = range ? toPercent(range[1], domain) : 0;

  return (
    <div className={styles.strip}>
      <div className={styles.header}>
        <span className={styles.label}>{label}</span>
        <span
          className={`${styles.value}${
            outOfRange ? ` ${styles.valueOutOfRange}` : ""
          }`}
        >
          {value}
          <span className={styles.unit}>{unit}</span>
        </span>
      </div>

      <div className={styles.track}>
        {range && (
          <span
            className={styles.band}
            style={{
              left: `${bandStart}%`,
              width: `${Math.max(0, bandEnd - bandStart)}%`,
              background: color,
              opacity: 0.18,
            }}
          />
        )}
        <span
          className={styles.marker}
          style={{
            left: `${markerLeft}%`,
            background: outOfRange ? OUT_OF_RANGE_COLOR : color,
          }}
        />
      </div>

      <div className={styles.scale}>
        <span>{domain[0]}</span>
        {range && (
          <span className={styles.rangeText}>
            {rangeLabel ? `${rangeLabel} ` : ""}
            {range[0]}–{range[1]}
          </span>
        )}
        <span>{domain[1]}</span>
      </div>
    </div>
  );
};

export default VitalsRangeStrip;
